import React, {useEffect, useState} from 'react'

interface OptionChipsProps {
  options: Array<string | {label?: string; id?: string; value?: string; name?: string}>
  onSelect: (value: string) => void
  selectedValue?: string | null
  theme?: 'light' | 'dark'
  /** Horizontal scroll row (default) or full-width vertical stack */
  layout?: 'horizontal' | 'vertical'
}

const OptionChips: React.FC<OptionChipsProps> = ({
  options,
  onSelect,
  selectedValue,
  theme = 'light',
  layout = 'horizontal',
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const getOptionLabel = (
    option: string | {label?: string; id?: string; value?: string; name?: string}
  ): string => {
    if (typeof option === 'string') return option
    return option.label || option.name || option.value || option.id || ''
  }

  const getOptionValue = (
    option: string | {label?: string; id?: string; value?: string; name?: string}
  ): string => {
    if (typeof option === 'string') return option
    return option.id || option.value || option.label || option.name || ''
  }

  const handleOptionClick = (
    option: string | {label?: string; id?: string; value?: string; name?: string}
  ) => {
    const value = getOptionValue(option)
    onSelect(value)
  }

  const isSelected = (
    option: string | {label?: string; id?: string; value?: string; name?: string}
  ): boolean => {
    const value = getOptionValue(option)
    return selectedValue === value
  }

  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>(theme)

  useEffect(() => {
    const detectTheme = () => {
      const bodyTheme = document.body.getAttribute('data-bs-theme')
      const htmlTheme = document.documentElement.getAttribute('data-bs-theme')
      const detectedTheme = bodyTheme || htmlTheme || theme
      setActualTheme(detectedTheme === 'dark' ? 'dark' : 'light')
    }

    detectTheme()

    const observer = new MutationObserver(detectTheme)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    })

    return () => observer.disconnect()
  }, [theme])

  const isDark = actualTheme === 'dark'
  const isVertical = layout === 'vertical'
  const primaryBlue = 'var(--bs-primary, #0d6efd)'

  const containerStyle: React.CSSProperties = isVertical
    ? {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        width: '100%',
        padding: 0,
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: 0,
      }
    : {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px',
        backgroundColor: isDark ? '#2d3748' : '#f5f5f5',
        borderRadius: '24px',
        border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(16,24,40,0.06)',
        width: '100%',
      }

  const listStyle: React.CSSProperties = isVertical
    ? {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        width: '100%',
      }
    : {
        display: 'flex',
        gap: '8px',
        flex: 1,
        overflowX: 'auto',
      }

  return (
    <div style={containerStyle} role='list' aria-label='Options'>
      <div style={listStyle}>
        {options.map((option, index) => {
          const label = getOptionLabel(option)
          const isOptionSelected = isSelected(option)
          const isHovered = hoveredIndex === index
          const highlighted = isOptionSelected || isHovered

          const buttonStyle: React.CSSProperties = isVertical
            ? {
                width: '100%',
                padding: '12px 16px',
                borderRadius: '10px',
                border: `2px solid ${primaryBlue}`,
                backgroundColor: highlighted
                  ? primaryBlue
                  : isDark
                    ? 'rgba(13, 110, 253, 0.08)'
                    : '#ffffff',
                color: highlighted ? '#ffffff' : isDark ? '#e5e7eb' : '#212529',
                fontSize: '0.95rem',
                fontWeight: highlighted ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                whiteSpace: 'normal',
                textAlign: 'left',
                lineHeight: 1.4,
                outline: 'none',
                boxSizing: 'border-box',
              }
            : {
                padding: '8px 16px',
                borderRadius: '20px',
                border: highlighted ? 'none' : isDark ? '1px solid #4a5568' : '1px solid #d1d5db',
                backgroundColor: highlighted ? '#007bff' : isDark ? '#374151' : '#f5f5f5',
                color: highlighted ? '#ffffff' : isDark ? '#e5e7eb' : '#6c757d',
                fontSize: '14px',
                fontWeight: highlighted ? '500' : '400',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                outline: 'none',
                boxSizing: 'border-box',
              }

          return (
            <button
              key={index}
              type='button'
              role='listitem'
              onClick={() => handleOptionClick(option)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={buttonStyle}
            >
              {label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default OptionChips
